import Joi, { ValidationResult } from 'joi';
import _ from 'lodash';

import {
    activeFromDateErrorSchema,
    activeToDateErrorSchema,
    childPacksWarningSchema,
    computeIsBundleWorthCovered,
    contentWarningSchema,
    displayName,
    features,
    packTypeSchema,
    scenesWarningSchema,
    sharedErrorNumericFields,
    sharedWarningFields,
} from './packValidation.shared';

import { PackType } from '../types/enums/packtype.enum';

import { Pack, PackUpdate, PriceLevel } from '../types/packTypes';

const sku = Joi.string()
    .regex(/^[a-z0-9][a-z0-9_.]+$/)
    .max(64)
    .messages({
        'string.pattern.base':
            'Product Id / SKU must start with a number or lowercase letter, and can contain numbers (0-9), lowercase letters (a-z), underscores (_) and periods (.)',
    });

const platformData = Joi.object().keys({
    store: Joi.string().required(),
    sku: sku.required(),
    active: Joi.boolean().required(),
    deprecatedSkus: Joi.array().items(Joi.string()),
});

const priceLevel = Joi.object().keys({
    productId: sku.required(),
    min: Joi.number().required(),
    max: Joi.number().required(),
    price: Joi.number().precision(2).min(0).max(1000).allow(null).allow(''),
    platformData: Joi.array().items(platformData),
});

const priceLevelComparator = (left: PriceLevel, right: PriceLevel) => {
    return left.min === right.min || left.max === right.max;
};

type PackValidationItem = PackUpdate & {
    activeToDateCopy: Pack['activeToDate'];
    childPacks: Pack[];
    isBundleWorthCovered?: boolean;
};

const createSchemas = (item: PackValidationItem) => {
    const { PLAYSET } = PackType;

    item.isBundleWorthCovered = computeIsBundleWorthCovered(
        item.childPacks,
        item.priceLevels,
    );

    const packWarningSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            ...sharedWarningFields,
            displayName,
            features,
            type: packTypeSchema(item.type),
            scenes: scenesWarningSchema,
            content: contentWarningSchema,
            childPacks: childPacksWarningSchema(
                item.requiredVersion,
                item.childPacks,
            ),
            locations: Joi.when('type', {
                is: PLAYSET,
                then: Joi.number().integer().required().min(0).max(100),
                otherwise: Joi.number().min(0).max(0).required(),
            }).label('Building count'),
            characters: Joi.when('type', {
                is: PLAYSET,
                then: Joi.number().integer().required().min(0).max(100),
                otherwise: Joi.number().min(0).max(0).required(),
            }).label('Character count'),
            templates: Joi.number().integer().required(),
            furniture: Joi.number().integer().required(),
            surfaceDesigns: Joi.number().integer().required(),
            garments: Joi.number().integer().required(),
            patterns: Joi.number().integer().required(),
            stickers: Joi.number().integer().required(),
            medias: Joi.array().items(Joi.number()),
            priceLevels: Joi.array().min(1).items(priceLevel).required(),
            isBundleWorthCovered: Joi.boolean()
                .invalid(false)
                .required()
                .messages({
                    'any.invalid':
                        'Bundle value must be represented as a min/max value',
                }),
        });

    const packErrorSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            packId: Joi.string()
                .pattern(/^[a-z0-9._]*$/)
                .required()
                .messages({
                    'string.empty': 'Pack ID can not be empty',
                    'string.pattern.base':
                        'Pack ID can only contain lowercase alphanumeric characters and dots',
                }),
            name: Joi.string().required().messages({
                'string.empty': 'String ID can not be empty',
            }),
            requiredVersion: Joi.number().required(),
            worth: Joi.number().integer(),
            priority: Joi.number().integer(),
            displayName: Joi.object(),
            features: Joi.object(),
            type: packTypeSchema(item.type),
            activeFromDate: activeFromDateErrorSchema,
            activeToDate: activeToDateErrorSchema,
            activeToDateCopy: Joi.any(),
            scenes: Joi.array().required(),
            content: Joi.array().required(),
            childPacks: Joi.array().items(Joi.object()).required(),
            ...sharedErrorNumericFields,
            outfits: Joi.number().integer(),
            accessories: Joi.number().integer(),
            hairStyles: Joi.number().integer(),
            medias: Joi.array().items(Joi.number()),
            tags: Joi.array().items(Joi.number().integer()),
            priceLevels: Joi.array()
                .min(1)
                .items(priceLevel)
                .unique(priceLevelComparator)
                .messages({
                    'array.unique': `Product ID '{#value.productId}' has a duplicate min or max value`,
                    'array.min': `Pack must contain at least 1 price level`,
                })
                .required(),
            carouselIds: Joi.array(),
            isBundleWorthCovered: Joi.boolean(),
        });

    return {
        packWarningSchema,
        packErrorSchema,
    };
};

function validate(
    item: PackUpdate,
    childPacksData: Pack[],
): {
    errors: ValidationResult;
    warnings: ValidationResult;
} {
    const validationItem: PackValidationItem = Object.assign(
        _.cloneDeep(item),
        {
            activeToDateCopy: item.activeToDate,
            childPacks: childPacksData,
        },
    );

    const { packWarningSchema, packErrorSchema } =
        createSchemas(validationItem);
    const warnings = packWarningSchema.validate(validationItem);
    const errors = packErrorSchema.validate(validationItem);
    return { errors, warnings };
}

export default validate;
